import { Tooltip } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  AlertCriticalIcon,
  AlertIcon,
  AlertInfoIcon,
  CanceledStatusIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import { formatCurrency } from '@fortress-technology-solutions/fortress-component-library/utils';
import { isNil } from 'lodash';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Field, FieldArray } from 'redux-form';
import generalMessages from '../../../App/messages';
import {
  renderSelectField,
  renderTextField,
} from '../../../../utils/redux-form-helper';
import {
  TextInputField,
  AssetDisposedDateRow,
  IncomeAssetCurrencyField,
  InterestRateField,
  SelectionColumn,
  YesNoColumns,
} from '../../SharedFormFields';
import { NEW_ALTERNATE_ROW, ENUM_ASSET_ROW_NAMES } from '../constants.js';
import componentMessages from '../messages';
import type { AssetIncomeTooltip, IncomeAssetRow } from '../types';

const messages = { ...generalMessages, ...componentMessages };

const COLORED_BG_ASSETS = [
  ENUM_ASSET_ROW_NAMES.CASH_HELD,
  ENUM_ASSET_ROW_NAMES.IRA,
  ENUM_ASSET_ROW_NAMES.K401,
  ENUM_ASSET_ROW_NAMES.KEOGH,
  ENUM_ASSET_ROW_NAMES.OTHER_RETIREMENT_ACCOUNT,
  ENUM_ASSET_ROW_NAMES.PENSION,
  ENUM_ASSET_ROW_NAMES.DISPOSED,
];

const OTHER_ASSET_NAMES = [
  ENUM_ASSET_ROW_NAMES.OTHER,
  ENUM_ASSET_ROW_NAMES.OTHER_RETIREMENT_ACCOUNT,
];

const ASSET_ACTUAL_IMPUTE_OPTIONS = (intl: Object) => [
  {
    text: intl.formatMessage(messages.choose),
    value: '-',
    disabled: true,
  },
  {
    text: intl.formatMessage(messages.actual),
    value: 'actual',
  },
  {
    text: intl.formatMessage(messages.impute),
    value: 'impute',
  },
];

const getAssetVerificationOptions = (stateRules) => [
  {
    text:
      stateRules?.selectAssetVerificationLabel || 'Select Asset Verification',
    value: -1,
    disabled: true,
  },
  ...((stateRules?.assetVerificationOptions || []).map((option) => ({
    text: option,
    value: option,
    disabled: false,
  })) ?? []),
];

const getOtherAssetTypeOptions = (stateRules) => [
  {
    text: stateRules?.selectOtherAssetTypeLabel || 'Select Asset Type',
    value: 'default',
    disabled: true,
  },
  ...((stateRules?.otherAssetTypeOptions || []).map((option) => ({
    text: option,
    value: option,
    disabled: false,
  })) ?? []),
];

const getInvestmentPropertyTypeOptions = (stateRules) => [
  {
    text: stateRules?.selectInvestmentPropertyTypeLabel || 'Select Asset Type',
    value: 'default',
    disabled: true,
  },
  ...((stateRules?.investmentPropertyTypeOptions || []).map((option) => ({
    text: option,
    value: option,
    disabled: false,
  })) ?? []),
];

const getType = (type, isList) => {
  if (isList) {
    return `.${type}`;
  }
  // Form keys are `${row}Asset${Tail}` (e.g. disposedAssetVerification). For field id
  // `assetVerification`, Tail must be `Verification` — not `AssetVerification` (which
  // would produce ...AssetAssetVerification and break PDF merge / persistence).
  const tail =
    type.startsWith('asset') && type.length > 5 ? type.slice(5) : type;
  return `Asset${tail.charAt(0).toUpperCase() + tail.slice(1)}`;
};

const getOtherFieldName = (assetName, hasAlternateColumns, index) => {
  if (assetName === 'other' && hasAlternateColumns) {
    return `otherList[${index}].otherName`;
  }
  if (assetName === ENUM_ASSET_ROW_NAMES.OTHER && !hasAlternateColumns) {
    return 'otherName';
  }
  if (assetName === ENUM_ASSET_ROW_NAMES.OTHER_RETIREMENT_ACCOUNT) {
    return 'otherRetirementAccountName';
  }
  return null;
};

const renderTooltip = (
  { type, message, icon }: AssetIncomeTooltip,
  values: Object,
) => {
  let tooltipIcon;
  switch (type) {
    case 'INFO':
      tooltipIcon = <AlertInfoIcon />;
      break;
    case 'WARNING':
      tooltipIcon = <AlertIcon />;
      break;
    case 'CRITICAL':
      tooltipIcon = <AlertCriticalIcon />;
      break;
    case 'ERROR':
      tooltipIcon = <CanceledStatusIcon />;
      break;
    case 'CUSTOM':
      tooltipIcon = icon;
      break;
    default:
      tooltipIcon = null;
      break;
  }
  return (
    <Tooltip
      placement="top"
      title={<FormattedMessage {...messages[message]} values={values} />}
      containerSX={{
        display: 'inline-block',
        verticalAlign: 'middle',
        marginLeft: '5px',
      }}
    >
      {tooltipIcon}
    </Tooltip>
  );
};

const AddAccountLink = ({
  clickHandler,
  fields,
  fieldLimit,
  disabled,
  additionalValues = {},
  colSize = 2,
}) => {
  return (
    <div className={`col-xs-${colSize}`}>
      <a
        onClick={() => clickHandler({ fields, fieldLimit, additionalValues })}
        disabled={disabled}
        className="btn-text ignore-react-onclickoutside"
        style={{ display: 'inline-block', marginTop: 4 }}
      >
        <i className={'icon et-plus'} />
        <span>Add Account</span>
      </a>
    </div>
  );
};

const DeleteAccountBtn = ({ clickHandler }) => {
  return (
    <a
      onClick={clickHandler}
      className="btn btn-delete-alt"
      style={{ marginTop: '3px', minWidth: '24px' }}
    >
      <i className="icon et-trash" />
    </a>
  );
};

const AssetTypeColumn = ({
  row,
  colSize,
  otherFieldName = 'otherAssetName',
  otherAssetTypeFieldName = null,
  investmentPropertyTypeFieldName = null,
  stateRules = null,
  isPrior = false,
  disabled = false,
  assetThreshold,
}: IncomeAssetRow) => {
  const { name, tooltip } = row;
  const colClass = colSize ? `col-xs-${colSize}` : 'col-xs-3';
  const tooltipValues = {
    AssetThreshold: isNil(assetThreshold)
      ? '$ ---'
      : formatCurrency(assetThreshold),
  };
  const showOtherAssetTypeRow =
    name === ENUM_ASSET_ROW_NAMES.OTHER &&
    stateRules?.requiresOtherAssetType &&
    otherAssetTypeFieldName;
  const showInvestmentPropertyTypeRow =
    name === ENUM_ASSET_ROW_NAMES.INVESTMENT_PROPERTIES &&
    stateRules?.requiresInvestmentPropertyType &&
    investmentPropertyTypeFieldName;

  return (
    <div className={colClass}>
      {showInvestmentPropertyTypeRow ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            gap: '8px',
            width: '100%',
            maxWidth: '100%',
          }}
        >
          <div style={{ flex: '1 1 0', minWidth: 0 }}>
            <FormattedMessage {...messages[name]} />
          </div>
          <div style={{ flex: '1 1 0', minWidth: 0 }}>
            <Field
              name={investmentPropertyTypeFieldName}
              component={renderSelectField}
              selectClassNames="input-md form-control"
              bsSize="lg"
              options={getInvestmentPropertyTypeOptions(stateRules)}
              disabled={disabled || isPrior}
              emptySelectValue="default"
            />
          </div>
        </div>
      ) : !OTHER_ASSET_NAMES.includes(name) ? (
        <FormattedMessage {...messages[name]} />
      ) : showOtherAssetTypeRow ? (
        <div className="ohio-other-asset-type-grid">
          {stateRules?.otherAssetTypeFieldLabel ? (
            <span className="control-label ohio-other-asset-type-grid__label">
              {stateRules.otherAssetTypeFieldLabel}
            </span>
          ) : (
            <span
              className="ohio-other-asset-type-grid__label"
              aria-hidden="true"
            />
          )}
          <div className="ohio-other-asset-type-grid__other">
            <Field
              name={otherFieldName}
              component={renderTextField}
              maxLength="35"
              className="input-lg"
              placeholder="Other"
              disabled={disabled || isPrior}
              required={!disabled}
            />
          </div>
          <div className="ohio-other-asset-type-grid__select">
            <Field
              name={otherAssetTypeFieldName}
              component={renderSelectField}
              selectClassNames="input-md form-control"
              bsSize="lg"
              options={getOtherAssetTypeOptions(stateRules)}
              disabled={disabled || isPrior}
              emptySelectValue="default"
            />
          </div>
        </div>
      ) : (
        <Field
          name={otherFieldName}
          component={renderTextField}
          maxLength="35"
          className="input-lg"
          placeholder="Other"
          disabled={disabled || isPrior}
          required={!disabled}
        />
      )}
      {isNil(tooltip) ? null : renderTooltip(tooltip, tooltipValues)}
    </div>
  );
};

const AlternateAssetField = ({
  asset,
  assetField,
  fields,
  intl,
  assetThreshold,
  message,
  largeInputColSize,
  mediumInputColSize,
  smallInputColSize,
  annualIncomeColSize,
  actualImputeColSize,
  assetVerificationColSize,
  handleAddAccount,
  noClicked,
  formFields,
  stateRules,
  calculateAnnualIncome,
  calculateCashValue,
  fieldsAnnualIncome,
  index = null,
  disabled,
  isPrior,
  isHotmaActive = false,
  isBankDataRequired = false,
  isSuitableForOccupancy = false,
  hasAlternateColumns,
}) => {
  const isList = typeof index === 'number' && !isNaN(index);
  const isAddedRow = isList && index > 0;
  const isOther = asset.name === ENUM_ASSET_ROW_NAMES.OTHER;
  const isFederalTaxRefundOrCredit =
    asset.name === ENUM_ASSET_ROW_NAMES.FEDERAL_TAX_REFUND_OR_CREDIT;
  const isCashHeld = asset.name === ENUM_ASSET_ROW_NAMES.CASH_HELD;
  const isSuitableForOccupancyAddedRow = isSuitableForOccupancy && isList;

  const fieldAnnualIncome = isList
    ? +fieldsAnnualIncome?.[asset.name]?.values?.[index]
    : fieldsAnnualIncome?.[asset.name];

  const typeOfAssetColSize = isBankDataRequired ? 2 : largeInputColSize;
  const resolvedAnnualIncomeColSize = annualIncomeColSize ?? largeInputColSize;
  const resolvedActualImputeColSize = actualImputeColSize ?? largeInputColSize;
  const resolvedAssetVerificationColSize =
    assetVerificationColSize ?? largeInputColSize;

  const otherFieldName = getOtherFieldName(
    asset.name,
    hasAlternateColumns,
    index,
  );
  const otherAssetTypeFieldName =
    asset.name === ENUM_ASSET_ROW_NAMES.OTHER && hasAlternateColumns
      ? `${assetField}.otherAssetType`
      : null;
  const investmentPropertyTypeFieldName =
    asset.name === ENUM_ASSET_ROW_NAMES.INVESTMENT_PROPERTIES &&
    hasAlternateColumns
      ? `${assetField}.investmentPropertyType`
      : null;
  const yesNoColumnName = isSuitableForOccupancyAddedRow
    ? `${assetField}.suitableForOccupancy`
    : asset.name;

  const rowClass =
    COLORED_BG_ASSETS.includes(asset.name) || isSuitableForOccupancyAddedRow
      ? 'row padbottom5 bg-accent2'
      : 'row padbottom5';
  const isOhioOtherAssetRow =
    isOther && stateRules?.requiresOtherAssetType && otherAssetTypeFieldName;
  const rowClassName = isOhioOtherAssetRow
    ? `${rowClass} ohio-other-asset-list-row`
    : rowClass;

  return (
    <>
      {asset.name === 'ira' && index === 0 && (
        <div className={rowClass}>
          <div className="col-xs-12 padbottom10">
            <FormattedMessage {...messages.retirementPlanAccount} />
          </div>
        </div>
      )}
      <div className={rowClassName}>
        {!!message ? (
          <div className={`col-xs-${typeOfAssetColSize}`}>
            <div className="row">
              <div className="col-xs-2" />
              <div className="col-xs-10">
                <FormattedMessage {...message} />
              </div>
            </div>
          </div>
        ) : isAddedRow &&
          !isOther &&
          asset.name !== ENUM_ASSET_ROW_NAMES.INVESTMENT_PROPERTIES ? (
          <div className={`col-xs-${typeOfAssetColSize}`} />
        ) : (
          <AssetTypeColumn
            row={asset}
            isPrior={isPrior}
            colSize={typeOfAssetColSize}
            disabled={disabled}
            assetThreshold={assetThreshold}
            otherFieldName={otherFieldName}
            otherAssetTypeFieldName={otherAssetTypeFieldName}
            investmentPropertyTypeFieldName={investmentPropertyTypeFieldName}
            stateRules={stateRules}
          />
        )}
        {isAddedRow && !isSuitableForOccupancy ? (
          <div className={`col-xs-${smallInputColSize}`} />
        ) : (
          <YesNoColumns
            name={yesNoColumnName}
            noClicked={() => {
              if (isSuitableForOccupancyAddedRow) {
                return;
              }

              noClicked(asset.name, isList || isSuitableForOccupancy);
            }}
            isPrior={isPrior}
            colSize={smallInputColSize}
          />
        )}
        {isBankDataRequired &&
          !isFederalTaxRefundOrCredit &&
          !isCashHeld &&
          !(isSuitableForOccupancy && !isList) && (
            <>
              <TextInputField
                name={assetField}
                type={getType('bankSource', isList)}
                disabled={disabled || isPrior}
                colSize={mediumInputColSize}
              />
              <TextInputField
                name={assetField}
                type={getType('accountNumber', isList)}
                disabled={disabled || isPrior}
                colSize={mediumInputColSize}
              />
            </>
          )}
        {isBankDataRequired && (isCashHeld || isFederalTaxRefundOrCredit) && (
          <div className={`col-xs-${mediumInputColSize * 2}`} />
        )}
        {!(isSuitableForOccupancy && !isList) && (
          <IncomeAssetCurrencyField
            name={assetField}
            type={getType('marketValue', isList)}
            disabled={disabled || isPrior}
            colSize={mediumInputColSize}
            onBlur={() => {
              if (!isOther) {
                calculateAnnualIncome(asset.name, isList, index);
                calculateCashValue(asset.name, isList, index);
              }
            }}
          />
        )}
        {!(isSuitableForOccupancy && !isList) && (
          <>
            <InterestRateField
              name={assetField}
              disabled={disabled || isPrior}
              type={getType('interestRate', isList)}
              colSize={smallInputColSize}
              onBlur={() => {
                if (!isOther) {
                  calculateAnnualIncome(asset.name, isList, index);
                }
              }}
            />
            <IncomeAssetCurrencyField
              name={assetField}
              type={getType('feesToConvertCash', isList)}
              disabled={disabled || isPrior}
              colSize={mediumInputColSize}
              onBlur={() => {
                if (!isOther) {
                  calculateCashValue(asset.name, isList, index);
                }
              }}
            />
            <IncomeAssetCurrencyField
              name={assetField}
              type={getType('cashValue', isList)}
              disabled={!isOther || disabled || isFederalTaxRefundOrCredit}
              colSize={mediumInputColSize}
            />
            <IncomeAssetCurrencyField
              name={assetField}
              type={getType('annualIncome', isList)}
              disabled={!isOther || disabled || isFederalTaxRefundOrCredit}
              colSize={resolvedAnnualIncomeColSize}
            />
            {stateRules?.requiresAssetVerification &&
              !isFederalTaxRefundOrCredit && (
                <SelectionColumn
                  name={assetField}
                  type={getType('assetVerification', isList)}
                  disabled={disabled || isPrior}
                  colSize={resolvedAssetVerificationColSize}
                  options={getAssetVerificationOptions(stateRules)}
                  emptySelectValue={-1}
                />
              )}
            {stateRules?.requiresAssetVerification &&
              isFederalTaxRefundOrCredit && (
                <div className={`col-xs-${resolvedAssetVerificationColSize}`} />
              )}
            {isHotmaActive && !isFederalTaxRefundOrCredit && (
              <SelectionColumn
                name={assetField}
                type={getType('actualImpute', isList)}
                disabled={
                  disabled || isPrior || (!isOther && fieldAnnualIncome > 0)
                }
                colSize={resolvedActualImputeColSize}
                options={ASSET_ACTUAL_IMPUTE_OPTIONS(intl)}
              />
            )}
            {isHotmaActive && isFederalTaxRefundOrCredit && (
              <div className={`col-xs-${resolvedActualImputeColSize}`} />
            )}
            {stateRules?.requiresAssetVerification && (
              <div className="col-xs-1">
                {isList && index !== 0 && !disabled && !isPrior ? (
                  <DeleteAccountBtn clickHandler={() => fields.remove(index)} />
                ) : null}
              </div>
            )}
            {!stateRules?.requiresAssetVerification &&
              isList &&
              index !== 0 &&
              !disabled &&
              !isPrior && (
                <div className="col-xs-1">
                  <DeleteAccountBtn clickHandler={() => fields.remove(index)} />
                </div>
              )}
          </>
        )}
      </div>
      {asset.name === 'disposed' ? (
        <div className="row container-fluid">
          <AssetDisposedDateRow
            disabled={disabled}
            intl={intl}
            colOffset="4"
            colSize="3"
          />
        </div>
      ) : null}
    </>
  );
};

const AlternateAssetFieldList = (props) => {
  const {
    fields,
    disabled,
    isExpandable,
    isSuitableForOccupancy,
    isPrior,
    handleAddAccount,
    asset,
    isBankDataRequired = false,
    largeInputColSize = 2,
  } = props;
  const isAddAccountDisabled = fields.length >= asset.fieldLimit;
  const typeOfAssetColSize = isBankDataRequired ? 2 : largeInputColSize;

  if (fields.length === 0) {
    fields.push({ ...NEW_ALTERNATE_ROW });
  }
  return (
    <>
      {fields.map((assetField, index) => (
        <AlternateAssetField
          {...props}
          index={index}
          assetField={assetField}
          key={`assetField-${index}`}
        />
      ))}
      {isExpandable && !disabled && !isPrior ? (
        <div className="row padbottom5">
          <AddAccountLink
            clickHandler={handleAddAccount}
            fields={fields}
            fieldLimit={asset.fieldLimit}
            disabled={isAddAccountDisabled}
            colSize={typeOfAssetColSize}
            {...(isSuitableForOccupancy
              ? { additionalValues: { suitableForOccupancy: null } }
              : {})}
          />
        </div>
      ) : null}
    </>
  );
};

const OldAssetRow = ({
  asset,
  fields,
  noClicked,
  isPrior,
  intl,
  assetThreshold,
  disabled,
  hasAlternateColumns,
}: Object) => {
  return (
    <div className="row faux-table__row" key={asset.name}>
      <AssetTypeColumn
        row={asset}
        isPrior={isPrior}
        disabled={disabled}
        assetThreshold={assetThreshold}
        otherFieldName={getOtherFieldName(asset.name, hasAlternateColumns)}
      />
      <YesNoColumns
        name={asset.name}
        noClicked={() => noClicked(asset.name)}
        isPrior={isPrior}
      />
      <IncomeAssetCurrencyField
        name={asset.name}
        type={'AssetValue'}
        disabled={disabled || isPrior}
      />
      <IncomeAssetCurrencyField
        name={asset.name}
        type={'AssetIncome'}
        disabled={disabled || isPrior}
      />
      <div className="col-xs-3">
        {!!asset.forms && !!messages[asset.forms] && (
          <FormattedMessage {...messages[asset.forms]} />
        )}
      </div>
      {asset.name === 'disposed' ? (
        <AssetDisposedDateRow disabled={disabled} intl={intl} colOffset="5" />
      ) : null}
    </div>
  );
};

export const AlternateAssetFieldRow = (props) => {
  const { assets, fields, isHotmaActive, hasAlternateColumns } = props;
  const { fields: _, ...propsWithoutFields } = props;
  return assets.map((asset) => {
    const disabled = fields[asset.name] !== 'yes';

    const isExpandable = asset?.expandable;
    const isSuitableForOccupancy =
      isHotmaActive &&
      (asset.name === ENUM_ASSET_ROW_NAMES.RENTAL_PROPERTY ||
        asset.name === ENUM_ASSET_ROW_NAMES.REAL_ESTATE);

    const showFieldArray =
      (isSuitableForOccupancy && !disabled) || !isSuitableForOccupancy;

    const message = isSuitableForOccupancy
      ? messages.suitableForOccupancy
      : null;

    const childProps = {
      ...propsWithoutFields,
      asset,
      assetField: asset.name,
      disabled,
    };

    // Compatibility with legacy assets that don't have alternate columns defined
    if (!hasAlternateColumns) {
      return <OldAssetRow key={asset.name} {...childProps} />;
    }

    return isExpandable ? (
      <React.Fragment key={asset.name}>
        {isSuitableForOccupancy ? (
          <AlternateAssetField
            {...childProps}
            isSuitableForOccupancy={isSuitableForOccupancy}
          />
        ) : null}
        {showFieldArray && (
          <FieldArray
            name={`${asset.name}List`}
            component={AlternateAssetFieldList}
            formFields={fields}
            isExpandable={isExpandable}
            isSuitableForOccupancy={isSuitableForOccupancy}
            message={message}
            {...childProps}
          />
        )}
      </React.Fragment>
    ) : (
      <AlternateAssetField key={asset.name} {...childProps} />
    );
  });
};
